/**
 * TimeRangeSelector Component
 *
 * Uses shadcn ToggleGroup for preset time ranges and
 * Calendar/Popover for custom date range selection.
 */

import * as React from 'react';
import { format, startOfDay, endOfDay } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { type DateRange } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/Calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';

export type TimeRangePreset = '1h' | '6h' | '12h' | '24h' | '7d' | 'custom';

export interface TimeRange {
  preset: TimeRangePreset;
  hours: number;
  startDate?: Date;
  endDate?: Date;
  label: string;
}

interface TimeRangeSelectorProps {
  value: TimeRange;
  onChange: (range: TimeRange) => void;
  className?: string;
}

const PRESETS: { value: TimeRangePreset; label: string; hours: number }[] = [
  { value: '1h', label: '1h', hours: 1 },
  { value: '6h', label: '6h', hours: 6 },
  { value: '12h', label: '12h', hours: 12 },
  { value: '24h', label: '24h', hours: 24 },
  { value: '7d', label: '7d', hours: 168 },
];

export function getDefaultTimeRange(): TimeRange {
  return {
    preset: '24h',
    hours: 24,
    label: '24h',
  };
}

export function getTimeRangeHours(range: TimeRange): number {
  if (range.preset === 'custom' && range.startDate && range.endDate) {
    return Math.ceil((range.endDate.getTime() - range.startDate.getTime()) / (1000 * 60 * 60));
  }
  return range.hours;
}

export function getTimeRangeDates(range: TimeRange): { start: Date; end: Date } {
  const end = new Date();
  if (range.preset === 'custom' && range.startDate && range.endDate) {
    return { start: range.startDate, end: range.endDate };
  }
  const start = new Date(end.getTime() - range.hours * 60 * 60 * 1000);
  return { start, end };
}

export const TimeRangeSelector: React.FC<TimeRangeSelectorProps> = ({
  value,
  onChange,
  className,
}) => {
  const [dateRange, setDateRange] = React.useState<DateRange | undefined>(
    value.startDate && value.endDate
      ? { from: value.startDate, to: value.endDate }
      : undefined
  );

  const handlePresetChange = (presetValue: string) => {
    if (!presetValue) return;

    const preset = PRESETS.find(p => p.value === presetValue);
    if (preset) {
      onChange({
        preset: preset.value,
        hours: preset.hours,
        label: preset.label,
      });
    }
  };

  const handleDateRangeSelect = (range: DateRange | undefined) => {
    setDateRange(range);
    if (range?.from && range?.to) {
      const start = startOfDay(range.from);
      const end = endOfDay(range.to);
      const hours = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60));
      onChange({
        preset: 'custom',
        hours,
        startDate: start,
        endDate: end,
        label: `${format(range.from, 'MMM d')} - ${format(range.to, 'MMM d')}`,
      });
    }
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Preset toggle group using shadcn ToggleGroup */}
      <ToggleGroup
        type="single"
        value={value.preset !== 'custom' ? value.preset : ''}
        onValueChange={handlePresetChange}
        variant="outline"
        size="sm"
      >
        {PRESETS.map((preset) => (
          <ToggleGroupItem
            key={preset.value}
            value={preset.value}
            aria-label={`Select ${preset.label} time range`}
            className="text-xs px-2.5"
          >
            {preset.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      {/* Custom date range picker using shadcn Popover/Calendar */}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant={value.preset === 'custom' ? 'secondary' : 'outline'}
            size="sm"
            className={cn(
              'h-8 gap-1.5 text-xs font-normal',
              value.preset === 'custom' && 'bg-accent'
            )}
          >
            <CalendarIcon className="h-3.5 w-3.5" />
            {value.preset === 'custom' ? (
              <span>{value.label}</span>
            ) : (
              <span>Custom</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="range"
            defaultMonth={dateRange?.from}
            selected={dateRange}
            onSelect={handleDateRangeSelect}
            numberOfMonths={2}
            disabled={(date) => date > new Date()}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default TimeRangeSelector;
